/**
 * Diagnóstico $0 de planos 2D para el demo "¿Sirve para presupuestar?": se lee el ARCHIVO real en el
 * navegador, con las reglas de cada formato (sin IA).
 *  - DXF: unidades declaradas ($INSUNITS), capas con nombre, bloques (INSERT), cotas (DIMENSION) y textos.
 *  - PDF: si trae dibujo vectorial (trazos) o solo una imagen (escaneado).
 */

/** Códigos de $INSUNITS del formato DXF (norma de Autodesk, no dato de obra). */
const UNIDADES_DXF: Record<number, string> = {
  0: '',
  1: 'pulgadas',
  2: 'pies',
  4: 'milímetros',
  5: 'centímetros',
  6: 'metros',
};

export interface DiagnosticoDxf {
  unidades: string | null;
  entidades: number;
  enCapasConNombre: number;
  capas: string[];
  bloques: number;
  cotas: number;
  textos: number;
  lineasSueltas: number;
}

export function diagnosticarDxf(texto: string): DiagnosticoDxf {
  const lineas = texto.split(/\r?\n/);
  const pares: [number, string][] = [];
  for (let i = 0; i + 1 < lineas.length; i += 2) pares.push([parseInt(lineas[i]!.trim(), 10), lineas[i + 1]!.trim()]);
  let unidades: string | null = null;
  const iu = pares.findIndex(([c, v]) => c === 9 && v === '$INSUNITS');
  if (iu >= 0) {
    const n = parseInt(pares[iu + 1]?.[1] ?? '0', 10);
    unidades = UNIDADES_DXF[n] || null;
  }
  // Solo la sección ENTITIES (lo dibujado), no las definiciones de bloques.
  let dentro = false;
  let tipo: string | null = null;
  const porCapa = new Map<string, number>();
  const porTipo = new Map<string, number>();
  let capaActual = '0';
  const cerrar = () => {
    if (!tipo) return;
    porTipo.set(tipo, (porTipo.get(tipo) ?? 0) + 1);
    porCapa.set(capaActual, (porCapa.get(capaActual) ?? 0) + 1);
  };
  for (let i = 0; i < pares.length; i++) {
    const [c, v] = pares[i]!;
    if (c === 2 && pares[i - 1]?.[0] === 0 && pares[i - 1]?.[1] === 'SECTION') dentro = v === 'ENTITIES';
    if (!dentro) continue;
    if (c === 0) {
      cerrar();
      tipo = v === 'ENDSEC' ? null : v;
      capaActual = '0';
      if (v === 'ENDSEC') dentro = false;
    } else if (c === 8) capaActual = v;
  }
  cerrar();
  const entidades = [...porTipo.values()].reduce((a, b) => a + b, 0);
  const sinNombre = (n: string) => n === '0' || n.toUpperCase() === 'DEFPOINTS';
  const capas = [...porCapa.keys()].filter((n) => !sinNombre(n));
  const enCapasConNombre = [...porCapa.entries()].filter(([n]) => !sinNombre(n)).reduce((a, [, k]) => a + k, 0);
  return {
    unidades,
    entidades,
    enCapasConNombre,
    capas,
    bloques: porTipo.get('INSERT') ?? 0,
    cotas: porTipo.get('DIMENSION') ?? 0,
    textos: (porTipo.get('TEXT') ?? 0) + (porTipo.get('MTEXT') ?? 0),
    lineasSueltas: porTipo.get('LINE') ?? 0,
  };
}

export interface DiagnosticoPdf {
  /** Trazos de dibujo (operador "l" = línea) en el contenido de la página. */
  trazos: number;
  imagenes: number;
  textos: number;
  vectorial: boolean;
}

/**
 * Lectura directa del PDF (sin librerías). Cubre contenidos sin comprimir, que es como vienen los de
 * ejemplo; un lector real descomprimiría los flujos antes de contar.
 */
export function diagnosticarPdf(bytes: Uint8Array): DiagnosticoPdf {
  let s = '';
  for (let i = 0; i < bytes.length; i++) s += String.fromCharCode(bytes[i]!);
  const trazos = (s.match(/[\d.]+ [\d.]+ l\b/g) ?? []).length;
  const imagenes = (s.match(/\/Subtype\s*\/Image/g) ?? []).length;
  const textos = (s.match(/\)\s*Tj/g) ?? []).length;
  return { trazos, imagenes, textos, vectorial: trazos > 0 };
}
